import { ReoccurringTemplateMeetingsDialog } from "@/components/agendaTemplate/ReoccurringTemplateMeetingsDialog";
import { RoleProtected } from "@/components/common/RoleProtected";
import { CreateMeetingDialog } from "@/components/meetings/CreateMeetingDialog";
import { ExportMeetingDialog } from "@/components/meetings/ExportMeetingDialog";
import { MeetingsTable } from "@/components/meetings/MeetingsTable";
import { PageHeader } from "@/components/page";
import { useRoleProtected } from "@/hooks/useRoleProtected";
import { useUserStore } from "@/stores/userStore";
import type { Meeting } from "@repo/types";
import { Button } from "@repo/ui/components/Base/Button";
import { DataTableWrapper } from "@repo/ui/components/Base/DataTable";
import {
  dataTableReverseContext,
  getAllTablesData,
  getAllTableVisibleFields,
} from "@repo/ui/components/Base/dataTableReverseContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@repo/ui/components/Base/DropDown";
import { useConstComponent } from "@repo/ui/components/Base/hooks/useNestedComponent";
import { ChevronDown, PlusCircleIcon } from "lucide-react";
import { useParams } from "react-router-dom";
import { MeetingsTiledView } from "./thumbnail/meetingsPreview";
import { ViewSwitchButton } from "./thumbnail/viewSwitchButton";
import { useState } from "react";
import { useAgendaTemplateList } from "@/hooks/queryHooks/agendaHooks";
import { useActiveTeamId } from "@/hooks/useActiveTeam";
import { useGetTenantByFriendly } from "@/hooks/queryHooks/tenantHooks";

export const MeetingsPage = () => {
  const { tenantId } = useParams();
  const activeTeam = useUserStore((s) => s.activeTeam);
  useRoleProtected("view");
  const { loaded: tenantLoaded, data: tenantData } = useGetTenantByFriendly(
    tenantId ?? ""
  );
  const [isTiledView, setIsTiledView] = useState(false);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createFromTemplateDialogOpen, setCreateFromTemplateDialogOpen] =
    useState(false);

  const { data: templates } = useAgendaTemplateList(
    tenantId ?? "",
    useActiveTeamId() || ""
  );

  const tableContext = dataTableReverseContext.useAsParent();
  const Provider = useConstComponent(tableContext.Provider);
  const getMeetings = () => getAllTablesData<Meeting>(tableContext);
  const getSelectedFields = () => getAllTableVisibleFields(tableContext);

  return (
    <div className="flex flex-col flex-grow">
      <PageHeader
        title={tenantData?.name ? `${tenantData.name}'s Meetings` : "Meetings"}
      >
        {activeTeam === "org" && (
          <p className="text-red-500 w-0 flex-grow-[3] mr-8 text-balance text-right">
            You must select a team to be able to edit and create meetings, and
            access linked agendas.
          </p>
        )}
        <div className="flex items-center flex-row gap-3">
          <ViewSwitchButton value={isTiledView} setValue={setIsTiledView} />
          <RoleProtected minRole="edit">
            <ExportMeetingDialog
              getMeetings={getMeetings}
              getPreselectedFields={getSelectedFields}
            />
          </RoleProtected>
          {tenantData?.id && (
            <RoleProtected minRole="edit">
              <div className="flex flex-row content-center">
                <Button
                  size={"xs"}
                  disabled={activeTeam === "org"}
                  className="align-middle rounded-r-none"
                  onClick={() => setCreateDialogOpen(true)}
                >
                  <p>Create</p>
                  <PlusCircleIcon className="w-4 ml-1" />
                </Button>
                <div className="w-[1px] h-6 bg-transparent" />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="xs"
                      className="rounded-l-none"
                      disabled={activeTeam === "org"}
                    >
                      <span className="sr-only">Open menu</span>
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem onClick={() => setCreateDialogOpen(true)}>
                      Create Meeting
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setCreateFromTemplateDialogOpen(true)}
                    >
                      Recurring Meetings from template
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <CreateMeetingDialog
                tenantId={tenantData.id}
                disabled={activeTeam === "org"}
                open={createDialogOpen}
                setOpen={setCreateDialogOpen}
              />
              <ReoccurringTemplateMeetingsDialog
                tenantId={tenantData.id}
                open={createFromTemplateDialogOpen}
                setOpen={setCreateFromTemplateDialogOpen}
                templates={templates}
              />
            </RoleProtected>
          )}
        </div>
      </PageHeader>
      {isTiledView && <MeetingsTiledView />}

      {!isTiledView && !tenantLoaded && <div>Loading ... </div>}

      {!isTiledView && tenantLoaded && tenantData?.id && (
        <DataTableWrapper>
          <Provider>
            <MeetingsTable tenantId={tenantData?.id} />
          </Provider>
        </DataTableWrapper>
      )}
    </div>
  );
};
